import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, DeviceMobileCamera, Check, X } from "@phosphor-icons/react";
import DigestPreview from "@/components/DigestPreview";

export default function Profile() {
  const { user, updateProfile, logout } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", notify_email: false, notify_sms: false, digest_include_details: false });
  const [status, setStatus] = useState({ email_enabled: false, sms_enabled: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({
      name: user.name || "",
      phone: user.phone || "",
      notify_email: user.notify_email ?? false,
      notify_sms: user.notify_sms ?? false,
      digest_include_details: user.digest_include_details ?? false,
    });
    api.get("/notify/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success("Profile saved");
    } finally { setSaving(false); }
  };

  const test = async (channel) => {
    try {
      const { data } = await api.post("/notify/test", { channel });
      if (data.sent) toast.success(`${channel.toUpperCase()} sent — check your inbox/phone`);
      else toast.error(data.note || "Not configured");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">Account</div>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold">Your profile</h1>
      </div>

      <form onSubmit={save} className="bg-white border border-[#EBE8E0] rounded-2xl p-6 space-y-4">
        <label className="block">
          <div className="text-sm font-medium mb-1.5">Email</div>
          <input value={user?.email || ""} disabled className="w-full h-11 rounded-xl border border-[#EBE8E0] bg-[#FDFBF7] px-4 text-[#576A62]" />
        </label>
        <label className="block">
          <div className="text-sm font-medium mb-1.5">Name</div>
          <input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full h-11 rounded-xl border border-[#EBE8E0] bg-white px-4 focus:ring-2 focus:ring-[#2C5545] outline-none" />
        </label>
        <label className="block">
          <div className="text-sm font-medium mb-1.5">Phone (for SMS reminders)</div>
          <input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+1 555-000-1212"
            className="w-full h-11 rounded-xl border border-[#EBE8E0] bg-white px-4 focus:ring-2 focus:ring-[#2C5545] outline-none" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-[#EBE8E0] px-4 py-3">
            <input type="checkbox" checked={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.checked })} data-testid="profile-notify-email" className="w-5 h-5 accent-[#2C5545]" />
            <span className="text-sm flex items-center gap-2"><EnvelopeSimple size={16} weight="duotone" /> Email reminders</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-[#EBE8E0] px-4 py-3">
            <input type="checkbox" checked={form.notify_sms} onChange={(e) => setForm({ ...form, notify_sms: e.target.checked })} data-testid="profile-notify-sms" className="w-5 h-5 accent-[#2C5545]" />
            <span className="text-sm flex items-center gap-2"><DeviceMobileCamera size={16} weight="duotone" /> SMS reminders</span>
          </label>
        </div>
        <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-[#EBE8E0] px-4 py-3">
          <input type="checkbox" checked={form.digest_include_details} onChange={(e) => setForm({ ...form, digest_include_details: e.target.checked })} data-testid="profile-digest-details" className="w-5 h-5 mt-0.5 accent-[#2C5545]" />
          <span className="text-sm">
            <span className="font-medium">Include care details in digest emails</span>
            <div className="text-[#576A62] mt-0.5">When enabled, weekly digests will contain patient names, medications, and appointment titles. When disabled (default), digests show only counts.</div>
          </span>
        </label>
        <div className="flex justify-end">
          <button data-testid="profile-save" type="submit" disabled={saving} className="rounded-full px-5 py-2.5 bg-[#2C5545] hover:bg-[#224235] text-white font-semibold">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>

      <div className="bg-white border border-[#EBE8E0] rounded-2xl p-6">
        <h2 className="font-heading text-xl font-bold">Notification delivery</h2>
        <p className="text-sm text-[#576A62] mt-1">Test that your reminders can actually be delivered.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-[#EBE8E0] p-4">
            <div className="flex items-center gap-2 font-medium">
              <EnvelopeSimple weight="duotone" /> Email {status.email_enabled ? <Check size={16} className="text-[#3E7B5C]"/> : <X size={16} className="text-[#C86A51]"/>}
            </div>
            <div className="text-xs text-[#576A62] mt-1">{status.email_enabled ? "Ready" : "Add a RESEND_API_KEY to backend/.env"}</div>
            <button onClick={() => test("email")} data-testid="test-email" className="mt-3 text-sm rounded-full border border-[#EBE8E0] hover:bg-[#EBE8E0]/60 px-4 py-1.5">Send test email</button>
          </div>
          <div className="rounded-xl border border-[#EBE8E0] p-4">
            <div className="flex items-center gap-2 font-medium">
              <DeviceMobileCamera weight="duotone" /> SMS {status.sms_enabled ? <Check size={16} className="text-[#3E7B5C]"/> : <X size={16} className="text-[#C86A51]"/>}
            </div>
            <div className="text-xs text-[#576A62] mt-1">{status.sms_enabled ? "Ready" : "Add Twilio credentials to backend/.env"}</div>
            <button onClick={() => test("sms")} data-testid="test-sms" className="mt-3 text-sm rounded-full border border-[#EBE8E0] hover:bg-[#EBE8E0]/60 px-4 py-1.5">Send test SMS</button>
          </div>
        </div>
      </div>

      <DigestPreview />

      <div>
        <button onClick={logout} data-testid="profile-logout" className="text-[#C86A51] hover:text-[#B05A43] font-semibold">Sign out</button>
      </div>
    </div>
  );
}
